/**
 * Watchlist Hook
 *
 * Manages a persistent watchlist of fund ISINs using localStorage.
 * Syncs across browser tabs via the storage event listener.
 */

import { useCallback, useSyncExternalStore } from "react";

// ============================================================================
// Constants
// ============================================================================

/** localStorage key for the watchlist */
const STORAGE_KEY = "compass-watchlist";

// ============================================================================
// Types
// ============================================================================

/**
 * Return type for the useWatchlist hook
 */
export interface UseWatchlistReturn {
  /** Array of ISINs currently in the watchlist */
  watchlist: string[];
  /** Add a fund ISIN to the watchlist */
  addToWatchlist: (isin: string) => void;
  /** Remove a fund ISIN from the watchlist */
  removeFromWatchlist: (isin: string) => void;
  /** Check if a fund ISIN is in the watchlist */
  isInWatchlist: (isin: string) => boolean;
  /** Toggle a fund ISIN in the watchlist (add if not present, remove if present) */
  toggleWatchlist: (isin: string) => boolean;
  /** Clear all items from the watchlist */
  clearWatchlist: () => void;
  /** Get the total number of items in the watchlist */
  watchlistCount: number;
}

// ============================================================================
// Storage Utilities
// ============================================================================

/**
 * Get the watchlist from localStorage
 */
function getStoredWatchlist(): string[] {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        // Ensure all items are strings
        return parsed.filter((item): item is string => typeof item === "string");
      }
    }
  } catch (error) {
    console.error("Failed to parse watchlist from localStorage:", error);
  }
  return [];
}

/**
 * Save the watchlist to localStorage
 */
function setStoredWatchlist(watchlist: string[]): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(watchlist));
  } catch (error) {
    console.error("Failed to save watchlist to localStorage:", error);
  }
}

// ============================================================================
// External Store for Cross-Tab Sync
// ============================================================================

/**
 * Subscribers for the external store
 */
const subscribers = new Set<() => void>();

/**
 * Cached snapshot for stable reference (prevents infinite re-renders)
 */
let cachedSnapshot: string[] = [];
let cachedSnapshotJson = "";

/**
 * Notify all subscribers of a change
 */
function notifySubscribers(): void {
  // Invalidate cache so next getSnapshot returns fresh data
  cachedSnapshotJson = "";
  subscribers.forEach((callback) => callback());
}

/**
 * Subscribe to watchlist changes
 */
function subscribe(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * Get the current snapshot of the watchlist
 * Returns a cached reference if data hasn't changed to prevent infinite loops
 */
function getSnapshot(): string[] {
  const stored = typeof window !== "undefined"
    ? localStorage.getItem(STORAGE_KEY) ?? "[]"
    : "[]";

  // Only create new array if data actually changed
  if (stored !== cachedSnapshotJson) {
    cachedSnapshotJson = stored;
    try {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        cachedSnapshot = parsed.filter((item): item is string => typeof item === "string");
      } else {
        cachedSnapshot = [];
      }
    } catch {
      cachedSnapshot = [];
    }
  }

  return cachedSnapshot;
}

/**
 * Server snapshot for SSR
 */
const serverSnapshot: string[] = [];

/**
 * Get the server snapshot (for SSR, returns empty array)
 */
function getServerSnapshot(): string[] {
  return serverSnapshot;
}

// ============================================================================
// Storage Event Listener (Cross-Tab Sync)
// ============================================================================

// Set up storage event listener once
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) {
      notifySubscribers();
    }
  });
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing a persistent watchlist of fund ISINs
 *
 * Features:
 * - Persists to localStorage
 * - Syncs across browser tabs
 * - Provides add/remove/toggle/check operations
 *
 * @returns Object with watchlist state and management functions
 *
 * @example
 * ```tsx
 * function FundCard({ fund }: { fund: Fund }) {
 *   const { isInWatchlist, toggleWatchlist } = useWatchlist();
 *   const isWatched = isInWatchlist(fund.isin);
 *
 *   return (
 *     <div>
 *       <h3>{fund.name}</h3>
 *       <button onClick={() => toggleWatchlist(fund.isin)}>
 *         {isWatched ? 'Remove from Watchlist' : 'Add to Watchlist'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * function WatchlistPage() {
 *   const { watchlist, watchlistCount, clearWatchlist } = useWatchlist();
 *
 *   return (
 *     <div>
 *       <h1>My Watchlist ({watchlistCount})</h1>
 *       {watchlist.map((isin) => (
 *         <FundCard key={isin} isin={isin} />
 *       ))}
 *       <button onClick={clearWatchlist}>Clear All</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useWatchlist(): UseWatchlistReturn {
  // Use useSyncExternalStore for cross-tab sync with stable snapshot
  const watchlist = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  /**
   * Add a fund ISIN to the watchlist
   */
  const addToWatchlist = useCallback((isin: string): void => {
    const current = getStoredWatchlist();
    if (!current.includes(isin)) {
      const updated = [...current, isin];
      setStoredWatchlist(updated);
      notifySubscribers();
    }
  }, []);

  /**
   * Remove a fund ISIN from the watchlist
   */
  const removeFromWatchlist = useCallback((isin: string): void => {
    const current = getStoredWatchlist();
    if (current.includes(isin)) {
      const updated = current.filter((item) => item !== isin);
      setStoredWatchlist(updated);
      notifySubscribers();
    }
  }, []);

  /**
   * Check if a fund ISIN is in the watchlist
   */
  const isInWatchlist = useCallback(
    (isin: string): boolean => {
      return watchlist.includes(isin);
    },
    [watchlist]
  );

  /**
   * Toggle a fund ISIN in the watchlist
   * @returns true if the item was added, false if it was removed
   */
  const toggleWatchlist = useCallback((isin: string): boolean => {
    const current = getStoredWatchlist();
    const isCurrentlyInWatchlist = current.includes(isin);

    if (isCurrentlyInWatchlist) {
      const updated = current.filter((item) => item !== isin);
      setStoredWatchlist(updated);
      notifySubscribers();
      return false;
    } else {
      const updated = [...current, isin];
      setStoredWatchlist(updated);
      notifySubscribers();
      return true;
    }
  }, []);

  /**
   * Clear all items from the watchlist
   */
  const clearWatchlist = useCallback((): void => {
    setStoredWatchlist([]);
    notifySubscribers();
  }, []);

  return {
    watchlist,
    addToWatchlist,
    removeFromWatchlist,
    isInWatchlist,
    toggleWatchlist,
    clearWatchlist,
    watchlistCount: watchlist.length,
  };
}

export default useWatchlist;
